import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import styles from './DProhServiceProvidersPage.module.scss'
import { getProhAttorneys, getProhOthers, downloadProhAttorneys, downloadProhOthers } from '../../api/otc/directory/service-provider'
import { template, fetch, expand } from '../../api/helper'

import Flexgrid from '../../components/Flexgrid'
import Table from '../../components/Table'
import TableTabs from '../../components/TableTabs'
import Loading from '../../components/Loading'
import DirSearch from '../../components/DirSearch'
import PageTitle from '../../components/PageTitle'
import HeadTitle from '../../components/HeadTitle'
import More from '../../components/More'
import Outline from '../../components/Outline'
import ExternalLink from '../../components/ExternalLink'
import OtherDirectories from '../../components/OtherDirectories'

const Locations = ({ value }) => value && value.length ? value.map((item, i) => <div key={i}>{item}</div>) : null

Locations.propTypes = {
  value: PropTypes.array
}

const LegalOrders = ({ value }) => {
  if (!value || !value.length) return null
  return value.map((item, i) => {
    if (!item) return null
    let urlLocation = item.toLowerCase().indexOf('http://')
    if (urlLocation === -1) urlLocation = item.toLowerCase().indexOf('https://')
    if (urlLocation === -1) return <div key={i}>{item}</div>
    const text = item.substring(0, urlLocation).trim()
    const url = item.substring(urlLocation).trim()
    return <div key={i}><ExternalLink href={url}>{text}</ExternalLink></div>
  })
}

LegalOrders.propTypes = {
  value: PropTypes.array
}

const prohAttorneysColumns = [
  {
    name: 'fullName',
    header: 'Name'
  },
  {
    name: 'firm',
    header: 'Firm'
  },
  {
    name: 'legalOrders',
    header: 'Legal order',
    component: LegalOrders,
    hideOnTablet: true,
    hideOnMobile: true
  },
  {
    name: 'locations',
    header: 'Location',
    component: Locations,
    hideOnMobile: true
  },
  {
    name: 'prohibitDate',
    header: 'Prohibited date'
  }
]

const prohOthersColumns = [
  {
    name: 'firm',
    header: 'Prohibited Individual or Entity'
  },
  {
    name: 'legalOrders',
    header: 'Legal order',
    component: LegalOrders,
    hideOnTablet: true,
    hideOnMobile: true
  },
  {
    name: 'locations',
    header: 'Location',
    component: Locations,
    hideOnMobile: true
  },
  {
    name: 'prohibitDate',
    header: 'Prohibited date'
  }
]

export default class DProhServiceProvidersPage extends PureComponent {
  state = {
    prohAttorneys: template('records'),
    prohOthers: template('records'),
    searchQuery: null,
    selectedTab: 'proh-attorneys'
  }

  componentDidMount () {
    this.loadData()
  }

  loadData = () => {
    fetch(this, getProhAttorneys, 'prohAttorneys', {
      page: 1,
      pageSize: 1000,
      search: this.state.searchQuery
    })

    fetch(this, getProhOthers, 'prohOthers', {
      page: 1,
      pageSize: 1000,
      search: this.state.searchQuery
    })
  }

  onSearch = searchQuery => {
    this.setState({ searchQuery }, this.loadData)
  }

  render () {
    const { prohAttorneys, prohOthers } = this.state

    const layouts = {
      default: [
        {
          type: 'flex',
          column: true,
          subtree: ['search', 'info', 'proh-attorneys', 'other-service-providers', 'other-directories']
        }
      ],
      lg: [
        {
          type: 'flex',
          subtree: [
            {
              type: 'side',
              subtree: ['search', 'info', 'other-directories']
            },
            {
              type: 'main',
              mt: '1.8rem',
              subtree: ['proh-attorneys', 'other-service-providers']
            }
          ]
        }
      ]
    }

    const dirSearch = <DirSearch key='search' onSearch={this.onSearch} value={this.state.searchQuery} />

    const otherDirectories = <div key='other-directories' className={styles.otherDirectories}>
      <OtherDirectories />
    </div>

    const attorneyCount = prohAttorneys.data.totalRecords ?? 0;
    const othersCount = prohOthers.data.totalRecords ?? 0;

    return (
      <div className={styles.component}>
        <HeadTitle title='Directory - Prohibited Service Providers' />
        <PageTitle>Prohibited Service Providers</PageTitle>
        <Flexgrid layouts={layouts}>
          {dirSearch}
          <div key='proh-attorneys' className={styles.table}>
            <Outline mode='heading'>
              Prohibited Service Providers
            </Outline>
            <TableTabs
              tabs={[{
                key: 'proh-attorneys',
                title: `Attorneys (${attorneyCount})`,
                func: () => {
                  this.setState({ selectedTab: 'proh-attorneys' })
                }
              }, {
                key: 'other-service-providers',
                title: `Accountants and Others (${othersCount})`,
                func: () => {
                  this.setState({ selectedTab: 'other-service-providers' })
                }
              }]}
              selectedTab={this.state.selectedTab} />
            {this.state.selectedTab === 'proh-attorneys' && <Loading
              type='table'
              emptyMessage={'No service providers found.'}
              error={prohAttorneys.error}
              data={prohAttorneys.data.records}
              reloading={prohAttorneys.reloading}
              loaded={prohAttorneys.loaded}>
              <Table
                columns={prohAttorneysColumns}
                loaded={prohAttorneys.loaded}
                data={prohAttorneys.data.records}
                showHeader
              />
              <More
                onClick={expand(this, getProhOthers, 'prohOthers')}
                disabled={prohOthers.expandEmpty} />
            </Loading>}
            {this.state.selectedTab === 'other-service-providers' && <Loading
              type='table'
              emptyMessage={'No service providers found.'}
              error={prohOthers.error}
              data={prohOthers.data.records}
              reloading={prohOthers.reloading}
              loaded={prohOthers.loaded}>
              <Table
                columns={prohOthersColumns}
                loaded={prohOthers.loaded}
                data={prohOthers.data.records}
                showHeader
              />
              <More
                onClick={expand(this, getProhOthers, 'prohOthers')}
                disabled={prohOthers.expandEmpty} />
            </Loading>}
          </div>
          {dirSearch}
          <div key='info' className={styles.info}>
            <p>
              The Prohibited Service Provider List identifies attorneys, accountants, auditors, investor relations firms, and other service providers that have been convicted of criminal activity, are subject to regulatory bans, suspensions or certain pending enforcement actions, or whose services OTC Markets Group has determined not to rely on. Companies engaging such providers may be ineligible to apply for, or remain on, the OTCQX or OTCQB markets.
            </p>
          </div>
          <div key='other-directories' className={styles.otherDirectories}>
            <OtherDirectories />
          </div>
        </Flexgrid>
      </div>
    )
  }
}

DProhServiceProvidersPage.propTypes = {
  value: PropTypes.array,
  children: PropTypes.node.isRequired,
  location: PropTypes.object
}
